import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as MagicIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lightbulb as InsightIcon,
  Assignment as RecommendationIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Psychology as StrategyIcon,
  Lightbulb as GuidanceIcon
} from '@mui/icons-material';
import * as supervisorApi from '../../services/supervisorApi';

function ConsultationSummaryReview({ 
  open, 
  onClose, 
  summaryData, 
  consultationId,
  token,
  onSaved
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedSummary, setEditedSummary] = useState(summaryData?.summary || '');
  const [editedInsights, setEditedInsights] = useState(summaryData?.keyInsights || []);
  const [editedRecommendations, setEditedRecommendations] = useState(summaryData?.recommendations || []);
  const [selectedCareNotes, setSelectedCareNotes] = useState([]);
  const [hasEdits, setHasEdits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize data when summaryData changes
  useEffect(() => {
    if (summaryData) {
      setEditedSummary(summaryData.summary || '');
      setEditedInsights(summaryData.keyInsights || []);
      setEditedRecommendations(summaryData.recommendations || []);
      // Pre-select all care notes by default
      if (summaryData.suggestedCareNotes) {
        setSelectedCareNotes(summaryData.suggestedCareNotes.map((_, index) => index));
      }
    }
  }, [summaryData]);

  // Track edits
  useEffect(() => {
    if (!summaryData) return;
    
    const edited = 
      editedSummary !== summaryData.summary ||
      JSON.stringify(editedInsights) !== JSON.stringify(summaryData.keyInsights) ||
      JSON.stringify(editedRecommendations) !== JSON.stringify(summaryData.recommendations);
    setHasEdits(edited);
  }, [editedSummary, editedInsights, editedRecommendations, summaryData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddInsight = () => {
    setEditedInsights([...editedInsights, '']);
  };

  const handleUpdateInsight = (index, value) => {
    const updated = [...editedInsights];
    updated[index] = value;
    setEditedInsights(updated);
  };

  const handleRemoveInsight = (index) => {
    setEditedInsights(editedInsights.filter((_, i) => i !== index));
  };

  const handleAddRecommendation = () => {
    setEditedRecommendations([...editedRecommendations, '']);
  };

  const handleUpdateRecommendation = (index, value) => {
    const updated = [...editedRecommendations];
    updated[index] = value;
    setEditedRecommendations(updated);
  };

  const handleRemoveRecommendation = (index) => {
    setEditedRecommendations(editedRecommendations.filter((_, i) => i !== index));
  };

  const handleToggleCareNote = (index) => {
    if (selectedCareNotes.includes(index)) {
      setSelectedCareNotes(selectedCareNotes.filter(i => i !== index));
    } else {
      setSelectedCareNotes([...selectedCareNotes, index]);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      // Save consultation summary with edit tracking
      const summaryPayload = {
        summary: editedSummary,
        keyInsights: editedInsights.filter(i => i.trim()),
        recommendations: editedRecommendations.filter(r => r.trim()),
        wasEdited: hasEdits
      };

      await supervisorApi.saveConsultationNotes(
        consultationId, 
        summaryPayload.summary, 
        summaryPayload.keyInsights, 
        summaryPayload.recommendations, 
        summaryPayload.wasEdited,
        token
      );

      // Activate selected care notes in place. The backend pre-creates each
      // suggested note as an inactive AI-suggestion subdoc and emits its
      // _id in summaryData; here we flip active=true on those existing
      // subdocs rather than POSTing fresh copies (which would duplicate).
      if (summaryData.suggestedCareNotes && selectedCareNotes.length > 0) {
        for (const index of selectedCareNotes) {
          const careNote = summaryData.suggestedCareNotes[index];
          if (careNote._id) {
            await supervisorApi.setCareNoteActive(consultationId, careNote._id, true, token);
          } else {
            // Legacy fallback for in-flight payloads emitted before the
            // backend started attaching _ids — preserves prior behaviour.
            await supervisorApi.createCareNote(consultationId, {
              type: careNote.type,
              content: careNote.content,
              priority: careNote.priority,
              applicableToAI: careNote.applicableToAI,
              applicableToHuman: careNote.applicableToHuman
            }, token);
          }
        }
      }

      if (onSaved) {
        onSaved({
          summary: summaryPayload,
          selectedCareNotes: selectedCareNotes.map(i => summaryData.suggestedCareNotes[i])
        });
      }

      onClose();
    } catch (err) {
      console.error('Error saving consultation summary:', err);
      setError(err.response?.data?.msg || 'Failed to save consultation summary');
    } finally {
      setIsSaving(false);
    }
  };

  const getCareNoteIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningIcon color="error" />;
      case 'strategy':
        return <StrategyIcon color="secondary" />;
      default:
        return <GuidanceIcon color="primary" />;
    }
  };

  const getCareNotePriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'success';
    }
  };

  if (!summaryData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MagicIcon color="primary" />
            <Typography variant="h6">AI-Generated Consultation Summary</Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Summary" />
          <Tab label="Key Insights" />
          <Tab label="Recommendations" />
          <Tab label="Care Notes" />
        </Tabs>

        {/* Summary Tab */}
        {activeTab === 0 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Consultation Summary
              </Typography>
              <Button
                startIcon={editMode ? <CancelIcon /> : <EditIcon />}
                onClick={() => setEditMode(!editMode)}
                size="small"
              >
                {editMode ? 'Cancel Edit' : 'Edit'}
              </Button>
            </Box>
            
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={8}
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                variant="outlined"
              />
            ) : (
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {editedSummary}
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Key Insights Tab */}
        {activeTab === 1 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Key Clinical Insights
              </Typography>
              <Button
                startIcon={<InsightIcon />}
                onClick={handleAddInsight}
                size="small"
                variant="outlined"
              >
                Add Insight
              </Button>
            </Box>
            
            <List>
              {editedInsights.map((insight, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <InsightIcon color="primary" />
                  </ListItemIcon>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={insight}
                      onChange={(e) => handleUpdateInsight(index, e.target.value)}
                      variant="standard"
                      sx={{ mr: 1 }}
                    />
                  ) : (
                    <ListItemText primary={insight} />
                  )}
                  {editMode && (
                    <IconButton onClick={() => handleRemoveInsight(index)} size="small">
                      <CloseIcon />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Recommendations Tab */}
        {activeTab === 2 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Recommendations
              </Typography>
              <Button
                startIcon={<RecommendationIcon />}
                onClick={handleAddRecommendation}
                size="small"
                variant="outlined"
              >
                Add Recommendation
              </Button>
            </Box>
            
            <List>
              {editedRecommendations.map((recommendation, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <RecommendationIcon color="secondary" />
                  </ListItemIcon>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={recommendation}
                      onChange={(e) => handleUpdateRecommendation(index, e.target.value)}
                      variant="standard"
                      sx={{ mr: 1 }}
                    />
                  ) : (
                    <ListItemText primary={recommendation} />
                  )}
                  {editMode && (
                    <IconButton onClick={() => handleRemoveRecommendation(index)} size="small">
                      <CloseIcon />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Care Notes Tab */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Suggested Care Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select which care notes to add to the client's treatment plan:
            </Typography>
            
            {summaryData.suggestedCareNotes && summaryData.suggestedCareNotes.length > 0 ? (
              summaryData.suggestedCareNotes.map((note, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedCareNotes.includes(index)}
                            onChange={() => handleToggleCareNote(index)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        }
                        label=""
                      />
                      {getCareNoteIcon(note.type)}
                      <Chip 
                        label={note.type.toUpperCase()} 
                        size="small" 
                        color={note.type === 'warning' ? 'error' : note.type === 'strategy' ? 'secondary' : 'primary'}
                      />
                      <Chip 
                        label={note.priority.toUpperCase()} 
                        size="small" 
                        color={getCareNotePriorityColor(note.priority)}
                        variant="outlined"
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1, mr: 2 }} noWrap>
                        {note.content.substring(0, 60)}...
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="body2" paragraph>
                        {note.content}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Chip 
                          label={`For AI: ${note.applicableToAI ? 'Yes' : 'No'}`} 
                          size="small"
                          color={note.applicableToAI ? 'success' : 'default'}
                          variant="outlined"
                        />
                        <Chip 
                          label={`For Human: ${note.applicableToHuman ? 'Yes' : 'No'}`} 
                          size="small"
                          color={note.applicableToHuman ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No care notes were suggested for this consultation.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || (!editedSummary.trim() && editedInsights.length === 0 && editedRecommendations.length === 0)}
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isSaving ? 'Saving...' : 'Save Summary & Notes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConsultationSummaryReview;